import type { LiveStream } from '../hooks/useLivestreams';
import { livestreamTrackingApi } from '../api/livestreamTracking';

interface TrackingEvent {
  eventName: string;
  livestreamId: string;
  properties?: Record<string, any>;
}

interface TrackingTimer {
  livestreamId: string;
  startTime: number;
  pausedTime: number;
  isPaused: boolean;
  totalPausedDuration: number;
}

class LivestreamTrackingService {
  private trackedViews: Set<string> = new Set();
  private trackingTimers: Map<string, TrackingTimer> = new Map();
  private isTrackingEnabled: boolean = true;
  private eventSlug: string | null = null;

  /**
   * Set the event slug for API calls
   */
  setEventSlug(slug: string): void {
    this.eventSlug = slug;
  }

  /**
   * Track a livestream view event
   * Prevents duplicate tracking for the same livestream in the same session
   */
  async trackView(livestream: { id: string; name?: string; sessionName?: string }, source?: string): Promise<void> {
    console.log('enabled:', this.isTrackingEnabled, 'eventSlug:', this.eventSlug);
    if (!this.isTrackingEnabled || !this.eventSlug) {
      console.warn('[LivestreamTracking] Tracking disabled or event slug not set');
      return;
    }

    const trackingKey = `view-${livestream.id}`;

    console.log(`[LivestreamTracking] Attempting to track view for livestream ${livestream.id} from ${source || 'unknown'}`);
    // Prevent duplicate view tracking in the same session
    if (this.trackedViews.has(trackingKey)) {
      console.log(`[LivestreamTracking] View already tracked for livestream ${livestream.id}`);
      return;
    }

    console.log(`[LivestreamTracking] Tracking view for livestream ${livestream.id} from ${source || 'unknown'}`);
    try {
      const response = await livestreamTrackingApi.trackEvent(
        this.eventSlug,
        livestream.id,
        {
          eventName: "$view",
          source: source || 'unknown',
          livestreamName: livestream.sessionName || livestream.name || 'Unnamed Livestream'
        }
      );

      if (response.success) {
        this.trackedViews.add(trackingKey);
        console.log(`[LivestreamTracking] Tracked view for livestream ${livestream.id} from ${source}`);
      } else {
        console.error('[LivestreamTracking] Failed to track view:', response.error);
      }
    } catch (error) {
      console.error('[LivestreamTracking] Failed to track view:', error);
    }
  }

  /**
   * Track external link click (for Zoom, etc.)
   */
  async trackExternalLinkClick(livestream: { id: string; link?: string; streamType?: string }, source?: string): Promise<void> {
    if (!this.isTrackingEnabled || !this.eventSlug) return;

    try {
      const response = await livestreamTrackingApi.trackEvent(
        this.eventSlug,
        livestream.id,
        {
          eventName: "$click",
          source: source || 'unknown',
          link: livestream.link,
          streamType: livestream.streamType
        }
      );

      if (response.success) {
        console.log(`[LivestreamTracking] Tracked external link click for livestream ${livestream.id}`);
      } else {
        console.error('[LivestreamTracking] Failed to track external link click:', response.error);
      }
    } catch (error) {
      console.error('[LivestreamTracking] Failed to track external link click:', error);
    }
  }

  /**
   * Track playback start
   */
  async trackPlaybackStart(livestreamId: string, isLive: boolean): Promise<void> {
    if (!this.isTrackingEnabled || !this.eventSlug) return;

    // Initialize timer for duration tracking
    this.trackingTimers.set(livestreamId, {
      livestreamId,
      startTime: Date.now(),
      pausedTime: 0,
      isPaused: false,
      totalPausedDuration: 0
    });

    try {
      const response = await livestreamTrackingApi.trackEvent(
        this.eventSlug,
        livestreamId,
        {
          eventName: "livestream_playback_start",
          isLive,
          timestamp: new Date().toISOString()
        }
      );

      if (response.success) {
        console.log(`[LivestreamTracking] Tracked playback start for livestream ${livestreamId}`);
      } else {
        console.error('[LivestreamTracking] Failed to track playback start:', response.error);
      }
    } catch (error) {
      console.error('[LivestreamTracking] Failed to track playback start:', error);
    }
  }

  /**
   * Track playback pause
   */
  async trackPlaybackPause(livestreamId: string): Promise<void> {
    if (!this.isTrackingEnabled || !this.eventSlug) return;

    const timer = this.trackingTimers.get(livestreamId);
    if (timer && !timer.isPaused) {
      timer.isPaused = true;
      timer.pausedTime = Date.now();
    }

    try {
      const response = await livestreamTrackingApi.trackEvent(
        this.eventSlug,
        livestreamId,
        {
          eventName: "livestream_playback_pause",
          timestamp: new Date().toISOString()
        }
      );

      if (response.success) {
        console.log(`[LivestreamTracking] Tracked playback pause for livestream ${livestreamId}`);
      } else {
        console.error('[LivestreamTracking] Failed to track playback pause:', response.error);
      }
    } catch (error) {
      console.error('[LivestreamTracking] Failed to track playback pause:', error);
    }
  }

  /**
   * Track playback resume
   */
  async trackPlaybackResume(livestreamId: string): Promise<void> {
    if (!this.isTrackingEnabled || !this.eventSlug) return;

    const timer = this.trackingTimers.get(livestreamId);
    if (timer && timer.isPaused) {
      timer.totalPausedDuration += Date.now() - timer.pausedTime;
      timer.isPaused = false;
    }

    try {
      const response = await livestreamTrackingApi.trackEvent(
        this.eventSlug,
        livestreamId,
        {
          eventName: "livestream_playback_resume",
          timestamp: new Date().toISOString()
        }
      );

      if (response.success) {
        console.log(`[LivestreamTracking] Tracked playback resume for livestream ${livestreamId}`);
      } else {
        console.error('[LivestreamTracking] Failed to track playback resume:', response.error);
      }
    } catch (error) {
      console.error('[LivestreamTracking] Failed to track playback resume:', error);
    }
  }

  /**
   * Track playback end (completion or manual stop)
   */
  async trackPlaybackEnd(livestreamId: string, reason: 'completed' | 'stopped' | 'error'): Promise<void> {
    if (!this.isTrackingEnabled || !this.eventSlug) return;

    const timer = this.trackingTimers.get(livestreamId);
    let watchDuration = 0;

    if (timer) {
      const totalTime = Date.now() - timer.startTime;
      watchDuration = Math.round((totalTime - timer.totalPausedDuration) / 1000); // in seconds
      this.trackingTimers.delete(livestreamId);
    }

    try {
      const response = await livestreamTrackingApi.trackEvent(
        this.eventSlug,
        livestreamId,
        {
          eventName: "livestream_playback_end",
          reason,
          watchDuration,
          timestamp: new Date().toISOString()
        }
      );

      if (response.success) {
        console.log(`[LivestreamTracking] Tracked playback end for livestream ${livestreamId} - Duration: ${watchDuration}s`);
      } else {
        console.error('[LivestreamTracking] Failed to track playback end:', response.error);
      }
    } catch (error) {
      console.error('[LivestreamTracking] Failed to track playback end:', error);
    }
  }

  /**
   * Track quality change
   */
  async trackQualityChange(livestreamId: string, quality: string): Promise<void> {
    let temporaryDisabled = true;
    if (!this.isTrackingEnabled || !this.eventSlug || temporaryDisabled) return;

    try {
      const response = await livestreamTrackingApi.trackEvent(
        this.eventSlug,
        livestreamId,
        {
          eventName: "livestream_quality_change",
          quality,
          timestamp: new Date().toISOString()
        }
      );

      if (response.success) {
        console.log(`[LivestreamTracking] Tracked quality change for livestream ${livestreamId}`);
      } else {
        console.error('[LivestreamTracking] Failed to track quality change:', response.error);
      }
    } catch (error) {
      console.error('[LivestreamTracking] Failed to track quality change:', error);
    }
  }

  /**
   * Track buffering events
   */
  async trackBuffering(livestreamId: string, duration: number): Promise<void> {
    let temporaryDisabled = true;
    if (!this.isTrackingEnabled || !this.eventSlug || temporaryDisabled) return;

    try {
      const response = await livestreamTrackingApi.trackEvent(
        this.eventSlug,
        livestreamId,
        {
          eventName: "livestream_buffering",
          duration,
          timestamp: new Date().toISOString()
        }
      );

      if (response.success) {
        console.log(`[LivestreamTracking] Tracked buffering for livestream ${livestreamId}`);
      } else {
        console.error('[LivestreamTracking] Failed to track buffering:', response.error);
      }
    } catch (error) {
      console.error('[LivestreamTracking] Failed to track buffering:', error);
    }
  }

  /**
   * Clear tracking for a specific livestream
   */
  clearTracking(livestreamId: string): void {
    this.trackingTimers.delete(livestreamId);
    this.trackedViews.delete(`view-${livestreamId}`);
  }

  /**
   * Clear all tracking data (useful for cleanup)
   */
  clearAllTracking(): void {
    this.trackedViews.clear();
    this.trackingTimers.clear();
  }

  /**
   * Enable or disable tracking
   */
  setTrackingEnabled(enabled: boolean): void {
    this.isTrackingEnabled = enabled;
  }

  /**
   * Check if tracking is available and properly configured
   */
  isAvailable(): boolean {
    return typeof window !== 'undefined' && !!this.eventSlug && this.isTrackingEnabled;
  }
}

// Export singleton instance
export const livestreamTracking = new LivestreamTrackingService();

// Export type for use in components
export type { LivestreamTrackingService };